import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const PipeBodyUtReportDetail: React.FC<{
  heatNumber: string;
  pipeLengthM?: number;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, pipeLengthM = 12.18, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const pbutMeasurements = measurements.filter((measurement) => measurement.reportType === 'PIPE_BODY_UT');
  const pipeLengthMm = pipeLengthM * 1000;
  const [activePbutId, setActivePbutId] = useState<string | null>(null);
  const effectiveFrequency = metadata?.frequency || frequency || 'per_pipe';
  const acceptanceLevelText = getProjectLimitText(projectLimitFields, 'acceptance_level', 'U2 (ITP)');
  const laminationAreaMax = getProjectLimitMax(projectLimitFields, 'pbut_lamination_area_mm2', 100);
  const inclusionAreaMax = getProjectLimitMax(projectLimitFields, 'pbut_inclusion_area_mm2', 50);
  const endZoneDepthMax = getProjectLimitMax(projectLimitFields, 'pbut_end_zone_depth_pct_wt', 6.4);
  const laminationAreaText = getProjectLimitText(projectLimitFields, 'pbut_lamination_area_mm2', `<= ${laminationAreaMax} mm2`);
  const inclusionAreaText = getProjectLimitText(projectLimitFields, 'pbut_inclusion_area_mm2', `<= ${inclusionAreaMax} mm2`);
  const endZoneDepthText = getProjectLimitText(projectLimitFields, 'pbut_end_zone_depth_pct_wt', `<= ${endZoneDepthMax} % WT`);
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['Acceptance level', acceptanceLevelText],
    ['Lamination area', laminationAreaText, true],
    ['End-zone depth', endZoneDepthText, true],
    ['Inclusion area', inclusionAreaText, true],
  ]);
  const getPbutIndicationStatus = (type: string, areaMm2: number, depthPctWt: number, endZone: boolean, disposition: string) => {
    if (disposition !== 'accept') return 'FAIL';
    if (type === 'inclusion' && areaMm2 > inclusionAreaMax) return 'FAIL';
    if (type !== 'inclusion' && areaMm2 > laminationAreaMax) return 'FAIL';
    if (endZone && depthPctWt > endZoneDepthMax) return 'FAIL';
    return 'PASS';
  };
  const indications = pbutMeasurements.map((measurement) => {
    const type = measurement.pbutIndicationType || 'lamination';
    const areaMm2 = Number(measurement.pbutAreaMm2) || 0;
    const depthPctWt = Number(measurement.pbutDepthPctWt) || 0;
    const endZone = measurement.pbutEndZone === 'yes';
    const disposition = measurement.pbutDisposition || 'accept';
    const maxArea = type === 'inclusion' ? 50 : 100;
    return {
      id: measurement.pbutIndicationId || measurement.id,
      type,
      posMm: Number(measurement.pbutPositionMm) || 0,
      clock: measurement.pbutClock || '2:30',
      areaMm2,
      depthPctWt,
      endZone,
      disposition,
      status: getPbutIndicationStatus(type, areaMm2, depthPctWt, endZone, disposition),
      measurement,
    };
  });
  useEffect(() => {
    if (!indications.length) {
      setActivePbutId(null);
      return;
    }

    if (!activePbutId || !indications.some((indication) => indication.id === activePbutId)) {
      setActivePbutId(indications[0].id);
    }
  }, [activePbutId, indications]);
  const laminations = indications.filter((indication) => indication.type === 'lamination');
  const inclusions = indications.filter((indication) => indication.type === 'inclusion');
  const failCount = indications.filter((indication) => indication.status === 'FAIL').length;
  const clockToY = (clock: string) => {
    const [hourRaw, minuteRaw = '0'] = clock.split(':');
    const hour = Number(hourRaw) || 0;
    const minute = Number(minuteRaw) || 0;
    const angle = (((hour % 12) + minute / 60) / 12) * 2 * Math.PI;
    return 50 - Math.cos(angle) * 30;
  };

  const renderRows = (rows: typeof indications, showDepth: boolean) => (
    <table className="w-full text-left">
      <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
        <tr>
          <th className="px-4 py-3">ID</th>
          <th className="px-4 py-3 text-right">Pos (mm)</th>
          <th className="px-4 py-3">Clock</th>
          <th className="px-4 py-3 text-right">Area mm2</th>
          {showDepth && <th className="px-4 py-3 text-right">Depth % WT</th>}
          {showDepth && <th className="px-4 py-3">End zone</th>}
          <th className="px-4 py-3">Dispo.</th>
          <th className="px-4 py-3">Status</th>
          <th className="px-4 py-3 text-right">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
        {rows.map((indication) => (
          <tr key={indication.id} onClick={() => indication.measurement && setActivePbutId(indication.id)} className={cn(indication.measurement && 'cursor-pointer hover:bg-page-bg', activePbutId === indication.id && 'bg-[#E7F3FF]')}>
            <td className="px-4 py-3 font-mono font-semibold">{indication.id}</td>
            <td className="px-4 py-3 text-right font-mono">{indication.posMm.toLocaleString()}</td>
            <td className="px-4 py-3 font-mono">{indication.clock}</td>
            <td className="px-4 py-3 text-right font-mono">{indication.areaMm2}</td>
            {showDepth && <td className="px-4 py-3 text-right font-mono">{indication.depthPctWt.toFixed(1)}</td>}
            {showDepth && <td className="px-4 py-3">{indication.endZone ? <span className="rounded-full bg-[#E7F3FF] px-2 py-1 text-[10px] font-semibold text-[#1F5B89]">end zone</span> : '-'}</td>}
            <td className="px-4 py-3">{indication.disposition}</td>
            <td className="px-4 py-3"><StatusBadge status={indication.status} /></td>
            <td className="px-4 py-3 text-right">
              {indication.measurement && (
                <ItemActionButtons measurement={indication.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">100% body scan</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">{acceptanceLevelText} · {pbutMeasurements.length} saved indication{pbutMeasurements.length === 1 ? '' : 's'}</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('PIPE_BODY_UT')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Indication
          </Button>
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-4">
            <div className="relative h-24 overflow-hidden rounded-full border border-[#D9DEE6] bg-gradient-to-b from-white via-[#EEF0F3] to-[#E2E5EA] shadow-inner">
              {[0, 0.25, 0.5, 0.75, 1].map((point) => (
                <div key={point} className="absolute bottom-0 top-0 w-px bg-[#D9DEE6]/70" style={{ left: `${point * 100}%` }} />
              ))}
              <span className="absolute bottom-3 left-5 font-mono text-[10px] text-heritage-grey">End 1</span>
              <span className="absolute bottom-3 right-5 font-mono text-[10px] text-heritage-grey">End 2</span>
              <span className="absolute right-6 top-2 rounded-full border border-[#B9DFF2] bg-[#E7F3FF] px-2 py-1 font-mono text-[10px] text-[#1F5B89]">full body</span>
              {indications.map((indication) => (
                <button
                  key={indication.id}
                  type="button"
                  title={`${indication.id} ${indication.type} ${indication.areaMm2} mm2`}
                  onClick={() => indication.measurement && setActivePbutId(indication.id)}
                  className={cn(
                    'absolute rounded-full border-2 border-white shadow-sm',
                    activePbutId === indication.id ? 'h-3.5 w-3.5 ring-2 ring-[#1F5B89]' : 'h-2.5 w-2.5',
                    indication.status === 'FAIL' ? 'bg-readable-red' : indication.status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green',
                    indication.measurement ? 'cursor-pointer' : 'cursor-default'
                  )}
                  style={{
                    left: `calc(${Math.max(1, Math.min(99, pipeLengthMm > 0 ? (indication.posMm / pipeLengthMm) * 100 : 0))}% - 5px)`,
                    top: `calc(${clockToY(indication.clock)}% - 5px)`,
                  }}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <span className="font-mono text-[10px] text-heritage-grey">0 mm</span>
              <span className="font-mono text-[10px] text-heritage-grey">End-zone limit · {endZoneDepthText}</span>
              <span className="ml-auto text-[11px] text-heritage-grey">{laminations.length} laminations · {inclusions.length} inclusions</span>
              <span className="font-mono text-[10px] text-heritage-grey">{pipeLengthM.toFixed(2)} m</span>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Laminations" />
            {renderRows(laminations, true)}
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Inclusions" />
            {renderRows(inclusions, false)}
          </CardContent>
        </Card>

        <div>
          <SectionHead title="Acceptance level comparison" subtitle="Project limits control the selected acceptance criteria" />
          <Card className="border-[#E2E5EA]">
            <CardContent className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
              <PipeBodyUtLevelCard title="U1 default" lamArea="<= 150 mm2" inclArea="<= 75 mm2" depth="<= 6.4 mm" />
              <PipeBodyUtLevelCard active title={`${acceptanceLevelText} - selected`} lamArea={laminationAreaText} inclArea={inclusionAreaText} depth={endZoneDepthText} />
            </CardContent>
          </Card>
        </div>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Indications', `${pbutMeasurements.length} saved`, true], ['Rejects', failCount, true]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};

const PipeBodyUtLevelCard: React.FC<{
  title: string;
  lamArea: string;
  inclArea: string;
  depth: string;
  active?: boolean;
}> = ({ title, lamArea, inclArea, depth, active }) => (
  <div className={cn('rounded-lg border p-4', active ? 'border-[#1F5B89] bg-[#E7F3FF]' : 'border-[#E2E5EA] bg-white')}>
    <div className="flex items-center justify-between gap-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{title}</div>
      {active && <span className="rounded-full border border-[#B9DFF2] bg-white px-2 py-1 text-[10px] font-semibold text-[#1F5B89]">active</span>}
    </div>
    <KeyValueRows rows={[
      ['Lamination area', lamArea, true],
      ['Inclusion area', inclArea, true],
      ['End-zone depth', depth, true],
    ]} />
  </div>
);